/**
 * Workbench — Janma Kundli generator.
 *
 * Form-only page: fill ChartInputForm, choose Standard / Pro view,
 * click Generate → PDF downloads. Mirrors the storefront's free
 * Janma Kundli flow but stays inside the Workbench so logged-in
 * professors can produce kundlis for any chart without going through
 * the storefront order flow.
 */
import { useCallback, useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import { ApiError, api } from '@/api/client';
import type { ChartCreateRequest } from '@/api/types';
import { ChartInputForm } from '@/components/ChartInputForm';
import { useLang } from '@/i18n/useLang';

export function JanmaKundliPage() {
  const { lang } = useLang();
  const t = (hi: string, en: string) => (lang === 'hi' ? hi : en);

  const [view, setView] = useState<'standard' | 'pro'>('standard');

  const downloadMut = useMutation<Blob, Error, ChartCreateRequest>({
    mutationFn: (req) => api.janmaKundliPdfBlob(req, lang, view),
    onSuccess: (blob, req) => {
      const name = (req.name ?? 'kundli').replace(/\s+/g, '_');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `janma_kundli_${name}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
  });

  const submitError = (() => {
    if (!downloadMut.error) return null;
    const e = downloadMut.error;
    if (e instanceof ApiError) {
      if (e.status === 422) return t('अमान्य इनपुट', 'Invalid input');
      if (e.status === 503) return t('PDF रेंडरर अस्थायी रूप से अनुपलब्ध', 'PDF renderer temporarily unavailable');
    }
    return e.message ?? 'Error';
  })();

  const onFormSubmit = useCallback(
    (req: ChartCreateRequest) => {
      downloadMut.mutate(req);
    },
    [downloadMut],
  );

  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-4 p-4 lg:gap-6 lg:p-6">
      <header className="rounded-2xl bg-stone-900/80 px-4 py-3 ring-1 ring-stone-800">
        <h1 className="text-lg font-semibold text-saffron-100">
          {t('जन्म-कुण्डली जनरेटर', 'Janma Kundli Generator')}
        </h1>
        <p className="mt-1 text-xs text-stone-400">
          {t(
            'जन्म-विवरण भरें — सम्पूर्ण शास्त्रीय गणना वाली PDF तुरंत डाउनलोड होगी।',
            'Fill in the birth details — the full classical computation PDF downloads instantly.',
          )}
        </p>
      </header>

      <div className="rounded-2xl bg-stone-900/60 p-4 ring-1 ring-stone-800">
        <p className="mb-3 text-xs font-medium uppercase tracking-wider text-saffron-400">
          {t('प्रस्तुति', 'Layout')}
        </p>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setView('standard')}
            className={`rounded-md border px-3 py-1.5 text-xs font-medium transition ${
              view === 'standard'
                ? 'border-saffron-600 bg-saffron-700/30 text-saffron-100'
                : 'border-stone-700 bg-stone-950 text-stone-400 hover:text-stone-100'
            }`}
          >
            {t('मानक (व्याख्यायुक्त)', 'Standard (with explanations)')}
          </button>
          <button
            type="button"
            onClick={() => setView('pro')}
            className={`rounded-md border px-3 py-1.5 text-xs font-medium transition ${
              view === 'pro'
                ? 'border-saffron-600 bg-saffron-700/30 text-saffron-100'
                : 'border-stone-700 bg-stone-950 text-stone-400 hover:text-stone-100'
            }`}
          >
            {t('प्रो (सघन, ज्योतिषी हेतु)', 'Pro (dense, practitioner)')}
          </button>
        </div>
        <p className="mt-2 text-[11px] text-stone-500">
          {t(
            'मानक संस्करण में संक्षिप्त व्याख्या-पंक्तियाँ होती हैं; प्रो में नहीं — डेटा एक ही है।',
            'Standard adds brief explanation lines; Pro hides them. The data is identical.',
          )}
        </p>
      </div>

      <ChartInputForm
        onSubmit={onFormSubmit}
        isSubmitting={downloadMut.isPending}
        submitError={submitError}
      />

      {downloadMut.isSuccess && (
        <div className="rounded-md bg-emerald-950/40 px-3 py-2 text-sm text-emerald-200 ring-1 ring-emerald-900">
          {t('PDF डाउनलोड हो गई।', 'PDF downloaded.')}
        </div>
      )}
    </div>
  );
}
